import { ApiFailure } from "@/components/data-display/api-failure";
import { LoadingTable } from "@/components/ui/system-state";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Plus, RefreshCw } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { FilterBar, FilterChips, FilterSearch, FilterSelect, type FilterChip } from "@/components/data-display/filter-bar";
import { EmptyState } from "@/components/ui/feedback";
import { PermissionGate } from "@/components/permission/permission-gate";
import { TRANSFER_LIST_COLUMNS, TransferRow } from "@/features/transfers/transfer-table";
import {
  TRANSFER_GROUP_LABEL,
  transferGroupOf,
  type TransferGroup,
} from "@/features/transfers/transfer-state";
import { useTransfers } from "@/hooks/use-transfers";
import { useWalletNames } from "@/hooks/use-wallet-names";
import { summarizeTransferUsd } from "@/features/transfers/transfer-usd-summary";
import { cn } from "@/lib/utils";
import type { Transfer } from "@/types";

/**
 * Transfers list (ALL-5793; flows TRF-102, OPS-03, DEV-03; page spec transfers-list).
 *
 * Founder, 2026-10-02: "this Transfer PAge needs to be redesigned and filled", and "Search field in
 * transfers doesnt work well for Asset etc fields."
 *
 * Data: GET /v1/transfers, newest first, 50 per page, "Load more" follows next_page_token. The
 * wallet, asset, network, date and text filters are all applied by the gateway before pagination
 * (ALL-5801: asset, network, created_after/created_before, q), so a filtered page is a page of the
 * filtered result set. The text query is debounced and only sent once it is 3+ characters, the
 * gateway's minimum. The status cards are a view over the loaded page; the `+` on a count means the
 * gateway has more pages to load *and* this group already has at least one visible transfer, so the
 * count is "N or more". A group with nothing loaded shows its plain count — never "0+".
 */

const ALL = "all";
const DATE_RANGES: { value: string; label: string; ms: number | null }[] = [
  { value: ALL, label: "Any time", ms: null },
  { value: "24h", label: "Last 24 hours", ms: 24 * 3600_000 },
  { value: "7d", label: "Last 7 days", ms: 7 * 24 * 3600_000 },
  { value: "30d", label: "Last 30 days", ms: 30 * 24 * 3600_000 },
];
const GROUPS: TransferGroup[] = ["awaiting-approval", "in-progress", "completed", "stopped"];
const GROUP_TONE: Record<TransferGroup, string> = {
  "awaiting-approval": "text-warning",
  "in-progress": "text-info",
  completed: "text-success",
  stopped: "text-danger",
};

function uniqueSorted(values: (string | undefined)[]): string[] {
  return [...new Set(values.map((v) => (v ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** Grow an option list with values the gateway has returned, never shrink it — a filter change
 *  narrows the page, and an option that vanished with it could not be selected back. */
function unionSorted(prev: string[], values: (string | undefined)[]): string[] {
  const next = uniqueSorted([...prev, ...values]);
  return next.length === prev.length && next.every((v, i) => v === prev[i]) ? prev : next;
}

/** The gateway requires 3–128 characters for `q`; anything shorter is not sent. */
const MIN_SEARCH_CHARS = 3;
const MAX_SEARCH_CHARS = 128;

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export default function TransfersPage() {
  const [walletId, setWalletId] = useState<string>(ALL);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<TransferGroup | typeof ALL>(ALL);
  const [asset, setAsset] = useState(ALL);
  const [network, setNetwork] = useState(ALL);
  const [range, setRange] = useState(ALL);
  // ALL-5830: a lab run's transfer belongs to a machine-named wallet, the rows the wallets page
  // has hidden by default since ALL-5795. Hidden here too, behind the same toggle wording.
  const [showLabActivity, setShowLabActivity] = useState(false);

  // The text filter is debounced, and only reaches the gateway at 3+ characters.
  const debouncedQuery = useDebounced(query, 300);
  const searchTerm = debouncedQuery.trim();
  const serverQ = searchTerm.length >= MIN_SEARCH_CHARS ? searchTerm.slice(0, MAX_SEARCH_CHARS) : undefined;

  // A date range becomes the half-open `created_after` instant the gateway filters on. It is
  // pinned to the selected range, not rebuilt every render, so paging stays on one filter set.
  const createdAfter = useMemo(() => {
    const ms = DATE_RANGES.find((r) => r.value === range)?.ms ?? null;
    return ms === null ? undefined : new Date(Date.now() - ms).toISOString();
  }, [range]);

  const { transfers, loading, error, refresh, hasMore, loadMore, loadingMore, loadMoreError } = useTransfers({
    walletId: walletId === ALL ? undefined : walletId,
    asset: asset === ALL ? undefined : asset,
    network: network === ALL ? undefined : network,
    q: serverQ,
    createdAfter,
  });
  const { wallets, byId } = useWalletNames();
  const now = Date.now();

  // Options come from what the gateway has returned; they only ever grow, so applying a filter
  // does not remove the value that was just selected.
  const [assetOptions, setAssetOptions] = useState<string[]>([]);
  const [networkOptions, setNetworkOptions] = useState<string[]>([]);
  useEffect(() => {
    setAssetOptions((prev) => unionSorted(prev, transfers.map((t) => t.asset)));
    setNetworkOptions((prev) => unionSorted(prev, transfers.map((t) => t.network)));
  }, [transfers]);

  /**
   * ALL-5830: split the loaded transfers into the operator's own activity and the test/lab
   * activity, using the wallet list this page already loads (`useWalletNames`, which carries the
   * same `isMachineNamedWallet` verdict the wallets page uses). Nothing is dropped: `transfers` is
   * untouched, and every hidden row returns the moment the toggle is expanded. A transfer whose
   * wallet is not in the list is unknown, not machine-named, so it stays visible.
   */
  const { namedTransfers, hiddenLabCount } = useMemo(() => {
    const named: Transfer[] = [];
    let hidden = 0;
    for (const t of transfers) {
      if (byId.get(t.wallet)?.machineNamed) hidden += 1;
      else named.push(t);
    }
    return { namedTransfers: named, hiddenLabCount: hidden };
  }, [transfers, byId]);

  const listedTransfers = showLabActivity ? transfers : namedTransfers;

  // The status tiles count what is shown, so they stay consistent with the rows below them.
  const groupCounts = useMemo(() => {
    const counts: Record<TransferGroup, number> = { "awaiting-approval": 0, "in-progress": 0, completed: 0, stopped: 0 };
    for (const t of listedTransfers) counts[transferGroupOf(t.state)] += 1;
    return counts;
  }, [listedTransfers]);

  const rows = useMemo(
    () => (group === ALL ? listedTransfers : listedTransfers.filter((t) => transferGroupOf(t.state) === group)),
    [listedTransfers, group],
  );

  // Every loaded transfer is test or lab activity: the empty state below says so and offers the
  // toggle, rather than claiming the filters matched nothing.
  const everythingHidden = !showLabActivity && transfers.length > 0 && namedTransfers.length === 0;

  // USD total of the visible rows, shown only once the gateway prices at least one of them;
  // until then a line of "no USD value" adds nothing an operator can act on.
  const usdSummary = useMemo(() => summarizeTransferUsd(rows), [rows]);

  const clearAll = () => {
    setGroup(ALL); setWalletId(ALL); setAsset(ALL); setNetwork(ALL); setRange(ALL); setQuery("");
  };

  const chips: FilterChip[] = [];
  if (group !== ALL) chips.push({ key: "group", label: `Status: ${TRANSFER_GROUP_LABEL[group]}`, clear: () => setGroup(ALL) });
  if (walletId !== ALL) chips.push({ key: "wallet", label: `Wallet: ${byId.get(walletId)?.name ?? walletId}`, clear: () => setWalletId(ALL) });
  if (asset !== ALL) chips.push({ key: "asset", label: `Asset: ${asset}`, clear: () => setAsset(ALL) });
  if (network !== ALL) chips.push({ key: "network", label: `Network: ${network}`, clear: () => setNetwork(ALL) });
  if (range !== ALL) chips.push({ key: "range", label: DATE_RANGES.find((r) => r.value === range)?.label ?? range, clear: () => setRange(ALL) });
  if (searchTerm) chips.push({ key: "q", label: `Search: “${searchTerm}”`, clear: () => setQuery("") });
  const filtersActive = chips.length > 0;

  // First run: nothing exists yet and no filter is narrowing the list (ALL-5737).
  const firstRun = !loading && !error && transfers.length === 0 && !filtersActive && !hasMore;
  const ready = !loading && !error;
  // A filter value the gateway rejects (400 TRANSFER_QUERY_INVALID) is not a service failure.
  const invalidFilter = error === "TRANSFER_QUERY_INVALID";

  return (
    <div>
      {ready && !firstRun && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4" role="group" aria-label="Filter by status">
          {GROUPS.map((g) => {
            const active = group === g;
            // "+" means the visible count is a lower bound: there is another page to
            // load AND this group already holds at least one visible transfer. A group
            // with nothing loaded reads its plain count, never the false promise "0+".
            const count = groupCounts[g];
            const showPlus = hasMore && count > 0;
            const plusHint = `At least ${count}; more transfers are available`;
            return (
              <button
                key={g}
                type="button"
                aria-pressed={active}
                onClick={() => setGroup(active ? ALL : g)}
                title={showPlus ? plusHint : undefined}
                className={cn(
                  "rounded-lg border bg-surface px-5 py-4 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                  active ? "border-primary ring-1 ring-primary" : "border-line",
                )}
              >
                <div className={cn("text-[26px] font-bold leading-[1.1] tracking-tight", count > 0 ? GROUP_TONE[g] : "text-ink")}>
                  {count}
                  {showPlus && (
                    <span className="ml-0.5 text-base font-semibold text-muted" aria-label={plusHint}>+</span>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted">{TRANSFER_GROUP_LABEL[g]}</div>
              </button>
            );
          })}
        </div>
      )}

      <FilterBar className="mb-3">
        <FilterSearch
          value={query}
          onChange={setQuery}
          placeholder="Search ID, wallet, address, asset, tx hash…"
          ariaLabel="Search transfers"
          className="max-w-[340px]"
        />
        <FilterSelect
          value={walletId}
          onChange={setWalletId}
          options={[{ value: ALL, label: "All wallets" }, ...wallets.map((w) => ({ value: w.id, label: w.name }))]}
          ariaLabel="Filter by wallet"
        />
        <FilterSelect
          value={asset}
          onChange={setAsset}
          options={[{ value: ALL, label: "All assets" }, ...assetOptions.map((a) => ({ value: a, label: a }))]}
          ariaLabel="Filter by asset"
        />
        <FilterSelect
          value={network}
          onChange={setNetwork}
          options={[{ value: ALL, label: "All networks" }, ...networkOptions.map((n) => ({ value: n, label: n.charAt(0).toUpperCase() + n.slice(1) }))]}
          ariaLabel="Filter by network"
        />
        <FilterSelect
          value={range}
          onChange={setRange}
          options={DATE_RANGES.map((r) => ({ value: r.value, label: r.label }))}
          ariaLabel="Filter by date"
        />
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-muted hover:text-ink"
          aria-label="Refresh transfers"
        >
          <RefreshCw className="size-3.5" aria-hidden />
        </button>
        <PermissionGate permission="transfer:create">
          <Link to="/transfers/new" className={cn(buttonVariants({ size: "sm" }), "ml-auto")}>
            <Plus className="size-4" aria-hidden /> Create Intent
          </Link>
        </PermissionGate>
      </FilterBar>

      <FilterChips chips={chips} onClearAll={clearAll} testId="transfer-filter-chips" />

      {loading && <LoadingTable rows={6} cols={TRANSFER_LIST_COLUMNS.length} label="Loading transfers" />}

      {error && (invalidFilter ? (
        <EmptyState
          card
          title="These filters can't be used"
          description="The gateway rejected the filter values. Clear them and try again."
          action={<Button variant="secondary" size="sm" onClick={clearAll}>Clear filters</Button>}
        />
      ) : (
        <ApiFailure error={error} subject="your transfers" onRetry={refresh} />
      ))}

      {firstRun && (
        <EmptyState
          card
          title="No transfers yet"
          description="Transfers you create or receive appear here."
          action={
            <PermissionGate permission="transfer:create">
              {/* OPS-03: on a genuine first run the empty state's action names the
                  thing the operator is about to do. "Create Intent" is the page's
                  internal shorthand (kept on the header CTA, which the lab depends
                  on); the first-run card offers plain "Create a transfer", matching
                  the approvals empty state's copy. Still inside the same
                  transfer:create gate, so a viewer without the permission sees no
                  action here. */}
              <Link to="/transfers/new" className={buttonVariants({ size: "sm" })}>
                <Plus className="size-4" aria-hidden /> Create a transfer
              </Link>
            </PermissionGate>
          }
        />
      )}

      {ready && !firstRun && usdSummary.pricedCount > 0 && (
        <p className="mb-3 text-sm text-muted" data-testid="transfers-usd-summary">
          {usdSummary.label}
        </p>
      )}

      {ready && !firstRun && (
        <TableWrapper className="max-md:overflow-visible max-md:border-0 max-md:bg-transparent">
          <Table className="dense max-md:block">
            <thead className="max-md:hidden">
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                {TRANSFER_LIST_COLUMNS.map((c) => (
                  <th key={c} className="px-3 py-2.5">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="max-md:block">
              {rows.map((t) => (
                <TransferRow
                  key={t.id}
                  transfer={t}
                  href={`/transfers/${encodeURIComponent(t.id)}`}
                  sourceName={byId.get(t.wallet)?.name}
                  now={now}
                />
              ))}
            </tbody>
          </Table>
          {rows.length === 0 && (everythingHidden ? (
            <EmptyState
              /* OPS-03/D02: this is not an empty org — the org has transfers, every
                 one of them test or lab activity the default view hides. "…so far"
                 says the list is not empty rather than that nothing exists, which is
                 what distinguishes it from the first-run card above. The action turns
                 the real toggle on; it is a user-visible filter, never a data change. */
              title="Only test and lab activity so far"
              description="Every transfer on this page belongs to a test or lab wallet. Show them to review the lab runs."
              action={
                <Button variant="secondary" size="sm" onClick={() => setShowLabActivity(true)}>
                  Show test and lab activity ({hiddenLabCount})
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No transfers match your filters"
              description="Nothing in the gateway's records matches the filters you have set."
              action={<Button variant="secondary" size="sm" onClick={clearAll}>Clear filters</Button>}
            />
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 max-md:border-0 max-md:px-0">
            <span className="text-xs text-muted" data-testid="transfers-count">
              Showing {rows.length} transfer{rows.length === 1 ? "" : "s"}
              {hasMore && " · more available"}
            </span>
            {hasMore && (
              <Button variant="secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            )}
          </div>
          {/* ALL-5830: the collapsed footer toggle, matching the wallets page (ALL-5795). Shown
              whenever there is something to reveal; when everything is hidden the empty state
              above carries the same control instead. */}
          {hiddenLabCount > 0 && !everythingHidden && (
            <div className="border-t border-line px-4 py-2.5 max-md:px-0">
              <button
                type="button"
                onClick={() => setShowLabActivity((shown) => !shown)}
                aria-expanded={showLabActivity}
                className="inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
              >
                <ChevronDown
                  aria-hidden
                  className={`size-3.5 transition-transform${showLabActivity ? " rotate-180" : ""}`}
                />
                {showLabActivity ? "Hide" : "Show"} test and lab activity ({hiddenLabCount})
              </button>
            </div>
          )}
          {loadMoreError && (
            <div className="border-t border-line px-4 py-3">
              <ApiFailure error={loadMoreError} subject="more transfers" onRetry={loadMore} />
            </div>
          )}
        </TableWrapper>
      )}
    </div>
  );
}
